import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import type { EmarSettings } from '@wodi/shared';
import { Button, LoadingBlock, Notice, PageHeader, Section, TextField } from '@wodi/ui';
import { useState } from 'react';

import { emarSettingsQuery, saveEmarSettings } from '../api/emar.ts';
import { can, sessionQuery } from '../api/session.ts';
import { useOnline } from '../hooks/use-online.ts';
import styles from '../inpatient/nursing.module.css';
import { errorText } from '../stock/common.tsx';
import { toasts } from '../toasts.ts';
import { SettingsPage } from './settings-nav.tsx';

// Settings > Medicine timing (docs/plans/phase2-emar-inpatient-pharmacy.md §2.4, E2): how
// early or late a dose may be given before the medicines chart flags it. Empty means not
// set: nothing is flagged early or late and no dose is "overdue". The clinical lead decides
// the numbers; Wodi suggests none. TODO(clinical-verify).

function minutesText(value: number | null): string {
  return value === null ? '' : String(value);
}

function readMinutes(text: string): number | null | 'invalid' {
  const trimmed = text.trim();
  if (trimmed === '') return null;
  if (!/^\d{1,4}$/.test(trimmed)) return 'invalid';
  const n = Number(trimmed);
  return n > 1440 ? 'invalid' : n;
}

function WindowsForm({ settings }: { settings: EmarSettings }) {
  const online = useOnline();
  const queryClient = useQueryClient();
  const [early, setEarly] = useState(minutesText(settings.earlyWindowMinutes));
  const [late, setLate] = useState(minutesText(settings.lateWindowMinutes));
  const earlyValue = readMinutes(early);
  const lateValue = readMinutes(late);
  const save = useMutation({
    mutationFn: () =>
      saveEmarSettings({
        earlyWindowMinutes: earlyValue === 'invalid' ? null : earlyValue,
        lateWindowMinutes: lateValue === 'invalid' ? null : lateValue,
      }),
    onSuccess: (next) => {
      queryClient.setQueryData(emarSettingsQuery.queryKey, next);
      toasts.show({ title: 'Medicine timing saved' });
    },
  });
  const invalid = 'Whole minutes from 0 to 1440, or empty for not set.';
  return (
    <form
      className={styles.form}
      aria-label="Medicine timing"
      onSubmit={(event) => {
        event.preventDefault();
        if (earlyValue !== 'invalid' && lateValue !== 'invalid') save.mutate();
      }}
    >
      <TextField
        label="Early window"
        unit="min"
        width="xs"
        inputMode="numeric"
        value={early}
        onChange={setEarly}
        description="A dose given more than this before its time is marked early. Empty: not set."
        {...(earlyValue === 'invalid' ? { errorMessage: invalid } : {})}
      />
      <TextField
        label="Late window"
        unit="min"
        width="xs"
        inputMode="numeric"
        value={late}
        onChange={setLate}
        description="A dose given more than this after its time is marked late, and one not recorded by then is overdue. Empty: not set."
        {...(lateValue === 'invalid' ? { errorMessage: invalid } : {})}
      />
      {save.error === null ? null : (
        <Notice tone="error">{errorText(save.error, 'Nothing was saved. Try again.')}</Notice>
      )}
      <div className={styles.actions}>
        <Button
          type="submit"
          isDisabled={!online || earlyValue === 'invalid' || lateValue === 'invalid'}
          isPending={save.isPending}
          pendingLabel="Saving…"
        >
          Save timing
        </Button>
      </div>
    </form>
  );
}

export function MedicinesSettingsPage() {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const settings = useQuery(emarSettingsQuery);
  if (session === null || !can(session, 'emar.settings.manage'))
    return (
      <>
        <PageHeader title="Medicine timing" />
        <Notice tone="info">
          You don't have permission to set medicine timing. Ask an administrator if you need it.
        </Notice>
      </>
    );
  return (
    <SettingsPage title="Medicine timing">
      <Section
        title="Dose windows"
        description="Doses are flagged, never refused. The clinical lead sets these; there is no default."
      >
        {settings.isPending ? <LoadingBlock label="Loading medicine timing" rows={2} /> : null}
        {settings.isError ? (
          <Notice
            tone="error"
            action={
              <Button dense onPress={() => void settings.refetch()}>
                Try again
              </Button>
            }
          >
            {errorText(settings.error, "Medicine timing couldn't be loaded. Try again.")}
          </Notice>
        ) : null}
        {settings.isSuccess ? <WindowsForm settings={settings.data} /> : null}
      </Section>
    </SettingsPage>
  );
}
